import { Directive, effect, ElementRef, inject, input, Renderer2 } from '@angular/core';

/**
 * Directive that makes `<img>` elements resilient to broken URLs (403, 404, etc.).
 *
 * Usage:
 * ```html
 * <img [omResilientSrc]="thumbnailUrl" [omFallbackSrc]="photoUrl" alt="..." />
 * ```
 *
 * On load error it swaps to `omFallbackSrc`. If that also fails, it hides the element.
 */
@Directive({
  selector: 'img[omResilientSrc]',
  host: { '(error)': 'onError()' },
})
export class ResilientImageDirective {
  readonly omResilientSrc = input<string | null | undefined>();
  readonly omFallbackSrc = input<string | null | undefined>();

  private readonly el = inject(ElementRef<HTMLImageElement>);
  private readonly renderer = inject(Renderer2);

  private usingFallback = false;
  private exhausted = false;

  constructor() {
    effect(() => {
      const primary = this.omResilientSrc();
      const fallback = this.omFallbackSrc();

      this.exhausted = false;
      this.renderer.removeStyle(this.el.nativeElement, 'visibility');

      if (primary) {
        this.usingFallback = false;
        this.setSrc(primary);
      } else if (fallback) {
        // Primary absent — start directly from fallback, skip redundant retry
        this.usingFallback = true;
        this.setSrc(fallback);
      }
    });
  }

  onError(): void {
    if (this.exhausted) return;

    const fallback = this.omFallbackSrc();
    if (!this.usingFallback && fallback && fallback !== this.omResilientSrc()) {
      this.usingFallback = true;
      this.setSrc(fallback);
    } else {
      this.exhausted = true;
      this.renderer.setStyle(this.el.nativeElement, 'visibility', 'hidden');
    }
  }

  private setSrc(url: string): void {
    this.renderer.setAttribute(this.el.nativeElement, 'src', url);
  }
}
